import { useCallback, useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import SwitchAccountOutlinedIcon from '@mui/icons-material/SwitchAccountOutlined';
import useImpersonateContext from 'src/hooks/useImpersonateContext';
import useAuth from 'src/hooks/useAuth';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

export default function ImpersonationBanner() {
    const { user } = useAuth();
    const {
        returning,
        onReturnToOriginal,
        isBannerOpen,
        closeBanner,
        setBannerHeight,
    } = useImpersonateContext();

    const observerRef = useRef<ResizeObserver | null>(null);

    const bannerRef = useCallback((el: HTMLDivElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (!el) return;

        const update = () => {
            const height = Math.ceil(el.getBoundingClientRect().height);
            if (height > 0) setBannerHeight(height);
        };

        update();
        observerRef.current = new ResizeObserver(update);
        observerRef.current.observe(el);
    }, [setBannerHeight]);

    if (!user?.impersonation || !isBannerOpen) return null;

    const companyName = user.customerName || user.customer?.businessName || 'Unknown Company';

    const role = user.roles?.[0]
        ? user.roles[0].charAt(0).toUpperCase() + user.roles[0].slice(1).toLowerCase()
        : 'User';

    const originalUserName = (user.impersonation.originalUserName || 'your account').toLowerCase();

    return (
        <Box
            ref={bannerRef}
            sx={{
                width: '100%',
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                px: 3,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                zIndex: (theme) => theme.zIndex.appBar + 10,
                position: 'sticky',
                top: 0,
                flexWrap: 'nowrap',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                <SwitchAccountOutlinedIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    You are viewing the dashboard as{' '}
                    <strong>{companyName}</strong>
                    {' — '}
                    <strong>{role}</strong>.{' '}
                    All write operations are disabled — read-only mode is active.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Button
                    size="small"
                    variant="contained"
                    onClick={onReturnToOriginal}
                    disabled={returning}
                    startIcon={returning ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={{
                        bgcolor: 'warning.contrastText',
                        color: 'warning.main',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        '&:hover': { bgcolor: 'warning.contrastText', opacity: 0.9 },
                        textTransform: 'none',
                    }}
                >
                    Return to {originalUserName}
                </Button>

                <IconButton
                    size="small"
                    onClick={closeBanner}
                    sx={{ color: 'warning.contrastText' }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}